/* eslint-disable react-refresh/only-export-components -- context/provider file */
import React, { createContext, useContext, useMemo } from 'react';
import type { BillingCycleRemittanceLink } from '../../services/api/types.gen';
import type { EoeClaimTabId } from './eoeClaimsManagerTabs';
import { EOEClaimsManagerTabContext } from './eoeClaimsManagerTabContext';

/** A selectable insurance/clinic option sourced from GET /billing-cycles/meta. */
export interface BillingOptionItem {
  id: string;
  name: string;
}

export interface EOEClaimsManagerOptions {
  insurances: BillingOptionItem[];
  clinics: BillingOptionItem[];
}

const EOEClaimsManagerOptionsContext = createContext<EOEClaimsManagerOptions>({
  insurances: [],
  clinics: [],
});

/** Read the agency-wide insurance/clinic option lists (populated from meta). */
export function useEOEClaimsManagerOptions(): EOEClaimsManagerOptions {
  return useContext(EOEClaimsManagerOptionsContext);
}

export function EOEClaimsManagerTabProvider({
  activeTabId,
  insurances = [],
  clinics = [],
  children,
}: {
  activeTabId: EoeClaimTabId;
  insurances?: BillingOptionItem[];
  clinics?: BillingOptionItem[];
  children: React.ReactNode;
}) {
  const options = useMemo<EOEClaimsManagerOptions>(
    () => ({ insurances, clinics }),
    [insurances, clinics]
  );
  return (
    <EOEClaimsManagerTabContext.Provider value={activeTabId}>
      <EOEClaimsManagerOptionsContext.Provider value={options}>
        {children}
      </EOEClaimsManagerOptionsContext.Provider>
    </EOEClaimsManagerTabContext.Provider>
  );
}

export interface EOEClaimsManagerNavigation {
  /**
   * Open the remittance detail for the linked advice(s). One link opens the
   * detail directly; more than one opens the short chooser.
   */
  openRemittance: (links: BillingCycleRemittanceLink[]) => void;
  /**
   * Invalidate any in-flight claim navigation so a late response cannot
   * switch views or open a worksheet the user already abandoned.
   */
  cancelClaimNavigation: () => void;
}

export const EOEClaimsManagerNavigationContext =
  createContext<EOEClaimsManagerNavigation>({
    // Fails loudly when a consumer renders outside the Billing Manager page,
    // which is the only place that can wire cross-tab navigation.
    openRemittance: () => {
      throw new Error(
        'useEOEClaimsManagerNavigation must be used within EOEClaimsManager'
      );
    },
    // Cancelling an in-flight navigation outside the page is a safe no-op:
    // there is no in-flight page navigation to invalidate.
    cancelClaimNavigation: () => {},
  });

/** Cross-tab navigation between the Claims and Remittances tabs. */
export function useEOEClaimsManagerNavigation(): EOEClaimsManagerNavigation {
  return useContext(EOEClaimsManagerNavigationContext);
}

export interface EOEClaimsManagerRegenerate {
  /** Re-run billing cycle creation for the agency, then reload the lists. */
  regenerate: () => void;
  isRegenerating: boolean;
}

/**
 * Null outside the Billing Manager page: the toolbar then renders no
 * regenerate control, so it never shows a button that cannot do anything.
 */
export const EOEClaimsManagerRegenerateContext =
  createContext<EOEClaimsManagerRegenerate | null>(null);

export function useEOEClaimsManagerRegenerate(): EOEClaimsManagerRegenerate | null {
  return useContext(EOEClaimsManagerRegenerateContext);
}
